import GolfOpenGroupsSection from '../components/GolfOpenGroupsSection'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import MemberMobileBookingHeader from '../components/MemberMobileBookingHeader'
import GolfDateCarousel from '../components/GolfDateCarousel'
import GolfCourseSplitLayout from '../components/GolfCourseSplitLayout'
import {
  DEFAULT_GOLF_TIME_ZONE,
  formatLongDate,
  formatMonthShort,
  formatTimeInTimeZone,
  formatWeekdayShort,
  getTodayDateKey,
  normalizeDateKey,
  parseDateKey,
  resolveCourseTimeZone,
} from '../utils/golfTime'

function formatDayLabel(dateKey, timeZone, locale) {
  const weekday = formatWeekdayShort(dateKey, timeZone, locale)
  const parsed = parseDateKey(dateKey)
  const dayNum = parsed ? parsed.getUTCDate() : ''
  return `${weekday || ''} ${dayNum}`.trim()
}

export default function MemberGolfCoursePage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { courseId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet } = useMemberPortalApi()
  const [courses, setCourses] = useState([])
  const [coursesLoading, setCoursesLoading] = useState(true)
  const [availabilityRevision, setAvailabilityRevision] = useState(0)
  const [slots, setSlots] = useState([])
  const [slotsLoading, setSlotsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)
  const [selectedDateKey, setSelectedDateKey] = useState(() => normalizeDateKey(searchParams.get('date')))

  const course = useMemo(() => courses.find((c) => c.id === courseId), [courseId, courses])
  const heroImageUrl = useMemo(() => course?.course_image_url || null, [course])

  useEffect(() => {
    let cancelled = false

    async function loadCourses() {
      setCoursesLoading(true)
      setError(null)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses`)
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_courses')
        }
        if (!cancelled) {
          setCourses(Array.isArray(json?.data) ? json.data : [])
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load courses', err)
          setError("portalPresentation.member_unable_to_load_this_course_right_now")
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false)
        }
      }
    }

    loadCourses()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet])

  useEffect(() => {
    const preferredTz = resolveCourseTimeZone(course || courses[0] || {})
    if (preferredTz && preferredTz !== timeZone) {
      setTimeZone(preferredTz)
    }
  }, [course, courses, timeZone])

  useEffect(() => {
    if (selectedDateKey) return
    const todayKey = getTodayDateKey(timeZone)
    if (todayKey) {
      setSelectedDateKey(todayKey)
    }
  }, [selectedDateKey, timeZone])

  useEffect(() => {
    let cancelled = false

    async function loadSlots() {
      if (!courseId || !selectedDateKey) return
      setSlotsLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/golf/courses/${courseId}/slots?date=${selectedDateKey}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_slots')
        }

        if (!cancelled) {
          setSlots(Array.isArray(json?.data) ? json.data : [])
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load golf slots', err)
          setError("portalPresentation.member_unable_to_load_tee_times_right_now")
        }
      } finally {
        if (!cancelled) {
          setSlotsLoading(false)
        }
      }
    }

    loadSlots()

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, memberPortalApiGet, selectedDateKey, availabilityRevision])

  useEffect(() => {
    if (!selectedDateKey) return

    const existingDate = searchParams.get('date')
    if (existingDate === selectedDateKey) return

    const next = new URLSearchParams(searchParams)
    next.set('date', selectedDateKey)
    navigate({ search: `?${next.toString()}` }, { replace: true })
  }, [navigate, searchParams, selectedDateKey])

  useEffect(() => {
    if (!coursesLoading && !course) {
      setError("portalPresentation.member_course_not_found_or_inactive")
    }
  }, [course, coursesLoading])

  return (
    <GolfCourseSplitLayout heroImageUrl={heroImageUrl}>
      <MemberMobileBookingHeader
        className="mp-golf-header"
        eyebrow={memberText("portalPresentation.member_golf")}
        desktopEyebrow={memberText("portalPresentation.member_available_tee_times")}
        title={course?.name || memberText("portalPresentation.member_select_a_course")}
        backLabel={memberText("portalPresentation.member_back_to_courses")}
        backTo={`/${clubCode}/golf`}
        meta={selectedDateKey ? <p className="mp-text-muted">{formatLongDate(selectedDateKey, timeZone, locale)}</p> : null}
        secondaryAction={<>
          <button
            type="button"
            className="mp-btn mp-btn--outline"
            onClick={() =>
              navigate(`/${clubCode}/golf/${courseId}/rates${selectedDateKey ? `?date=${selectedDateKey}` : ''}`, {
                state: { course, date: selectedDateKey },
              })
            }
            disabled={!courseId}
          > {memberText("portalPresentation.member_view_rates")} </button>
        </>}
      />

      <div className="mp-golf-scroll-area">
        <GolfDateCarousel
          bookingWindow={course?.booking_window}
          timeZone={timeZone}
          selectedDateKey={selectedDateKey}
          onSelectDate={setSelectedDateKey}
          formatPrimary={(dateKey) => formatDayLabel(dateKey, timeZone, locale)}
          formatSecondary={(dateKey) => formatMonthShort(dateKey, timeZone, locale)}
        />

        <div className="mp-tee-slot-scroll">
          <GolfOpenGroupsSection key={courseId} clubCode={clubCode} course={course} courseId={courseId} date={selectedDateKey} timeZone={timeZone} onChanged={() => setAvailabilityRevision(value => value + 1)} />
          {error ? <div className="mp-warning">{memberText(error)}</div> : null}

          {slotsLoading ? (
            <p className="mp-text-muted">{memberText("portalPresentation.member_loading_tee_times")}</p>
          ) : slots.length === 0 ? (
            <div className="mp-empty">{memberText("portalPresentation.member_no_available_tee_times_for_this_day")}</div>
          ) : (
            <div className="mp-tee-slot-list">
              {slots.map((slot) => (
                <div key={slot.id} className="mp-tee-slot">
                  <div>
                    <div className="mp-tee-slot__time">{formatTimeInTimeZone(slot.slot_time, timeZone, locale)}</div>
                    <div className="mp-tee-slot__meta">{memberText("portalPresentation.member_up_to")} {slot.capacity || 4} {memberText("portalPresentation.member_players_2")}</div>
                  </div>
                  <div className="mp-tee-slot__actions">
                    <button
                      type="button"
                      className="mp-btn mp-btn--primary"
                      onClick={() =>
                        navigate(`/${clubCode}/golf/${courseId}/book/${slot.id}?date=${selectedDateKey}`, {
                          state: { course, slot, date: selectedDateKey },
                        })
                      }
                    > {memberText("portalPresentation.member_book_now")} </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div> {/* scroll area */}
    </GolfCourseSplitLayout>
  )
}
